import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSharedState } from '../../hooks/SharedStateContext';
import { updatePreferredPronoun } from '../../utilities/firebase';

/**
 * Reusable pronoun selection dropdown component.
 * Used in both WelcomeModal and MoreOptionsModal.
 *
 * @param {Object} props
 * @param {string} props.value - Current pronoun value (optional, uses sharedState if not provided)
 * @param {function} props.onChange - Optional callback when value changes
 * @param {string} props.className - Additional CSS class for the container
 * @param {boolean} props.useLabel - Whether to show the label (default: true)
 */
function PronounSelect({ value, onChange, className = '', useLabel = true }) {
  const { t } = useTranslation();
  const { sharedState, setSharedState } = useSharedState();

  // Use provided value or fall back to sharedState
  const currentValue = value ?? sharedState.preferredPronoun ?? 'unspecified';

  const handleChange = (event) => {
    const newValue = event.target.value;

    // Update shared state
    setSharedState(prevState => ({
      ...prevState,
      preferredPronoun: newValue
    }));

    // Save to Firestore and localStorage
    updatePreferredPronoun(newValue);

    // Call optional external handler
    if (onChange) {
      onChange(newValue);
    }
  };

  return (
    <div className={className}>
      {useLabel && <label>{t('setting_preferred_pronoun')}</label>}
      {!useLabel && <span>{t('setting_preferred_pronoun')}</span>}
      <select value={currentValue} onChange={handleChange}>
        <option value="unspecified">{t('pronoun_unspecified')}</option>
        <option value="masculine">{t('pronoun_masculine')}</option>
        <option value="feminine">{t('pronoun_feminine')}</option>
        <option value="nonbinary">{t('pronoun_nonbinary')}</option>
      </select>
    </div>
  );
}

export default PronounSelect;
